import { getCalendarDayKey } from './calendar-day-key';
import {
  NO_CREDITS_EVENT_ERROR,
  SUBSCRIPTION_CANCELLED_EVENT_ERROR,
} from './events.constants';
import { getEventDate } from './get-event-date';

/**
 * Pure reminder-status logic shared between the customer web app's event card
 * and the monolith (which computes the status server-side for the Shopify app).
 *
 * The single source of truth for "is a reminder scheduled / sent / etc." Kept
 * free of React and rendering: each UI renders the returned status its own way.
 * All date-ish inputs accept either ISO strings (web app, over-the-wire) or
 * `Date` objects (monolith, straight from the DB); they are normalized here.
 */

export type EventReminderMessageType =
  | 'confirmation'
  | 'reminder'
  | 'follow_up';

export type EventReminderStatusReminder = {
  message_type: EventReminderMessageType;
  time_before: number;
  event_date: string | number;
  last_planner_status?: string | null;
  next_occurrence?: string | Date | null;
};

export type EventReminderStatusSentReminder = {
  message_type: EventReminderMessageType;
  time_before: number;
  event_date: string | number;
  occurrence_date: string | Date;
  last_sending_date: string | Date;
};

/**
 * The subset of an event needed to compute its reminder status. Mirrors the
 * fields the frontend `EventPreviewWithReminderData` exposes.
 */
export type EventReminderStatusInput = {
  active?: boolean;
  errors?: string[] | null;
  receiver_numbers?: string[] | null;
  suggested_numbers?: string[] | null;
  accepted_suggested_numbers?: string[] | null;
  suggestions_reviewed?: boolean | null;
  event_date: string | number;
  event_end_date?: string | number | null;
  google_event_date?: string;
  all_day?: boolean;
  /** Raw `calendars.timezone`: the zone the planner dates all-day occurrences in. */
  calendar_timezone?: string | null;
  reminders?: EventReminderStatusReminder[];
  sent_reminders?: EventReminderStatusSentReminder[];
};

/** The subset needed to tell whether one occurrence's reminder already went out. */
export type SentReminderOccurrenceInput = Pick<
  EventReminderStatusInput,
  'all_day' | 'calendar_timezone' | 'google_event_date' | 'sent_reminders'
>;

export type EventReminderStatus =
  | 'error'
  | 'inactive'
  | 'no_reminder'
  | 'activating'
  | 'review'
  | 'scheduled'
  | 'sent';

export type MessageSentAndNext = {
  lastConfirmation: string | null;
  nextConfirmation: string | null;
  lastReminder: string | null;
  nextReminder: string | null;
  lastFollowUp: string | null;
  nextFollowUp: string | null;
};

export type GetEventReminderStatusOptions = {
  /** Current time in epoch ms (injected to keep the function pure/testable). */
  now: number;
  /** External error flag (e.g. a failed render/save), forces `'error'`. */
  isError?: boolean;
  /**
   * Whether the event was just activated client-side. Web app supplies this
   * from a UI-local hook; server callers omit it, so `'activating'` never fires.
   */
  recentlyActivated?: boolean;
  /**
   * When the event's date already represents the reminder date, sent/scheduled
   * is derived purely from whether that date is in the past or future.
   */
  eventDateIsReminderDate?: boolean;
  /** The profile's `reminders_paused`; absent means not paused. */
  remindersPaused?: boolean;
};

const ALL_DAY_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const PAUSE_EVENT_ERRORS: ReadonlySet<string> = new Set([
  NO_CREDITS_EVENT_ERROR,
  SUBSCRIPTION_CANCELLED_EVENT_ERROR,
]);

/**
 * Computes the reminder status shown on an event/appointment. Precedence
 * (last wins): inactive -> sent/scheduled -> activating -> review -> error.
 */
export function getEventReminderStatus(
  event: EventReminderStatusInput,
  options: GetEventReminderStatusOptions,
): EventReminderStatus {
  const {
    now,
    isError,
    recentlyActivated = false,
    eventDateIsReminderDate = false,
    remindersPaused = false,
  } = options;

  const eventDate = getEventDate(event);

  let hasSentReminders: boolean;
  let hasScheduledReminders: boolean;
  if (eventDateIsReminderDate) {
    hasSentReminders = eventDate.getTime() < now;
    hasScheduledReminders = eventDate.getTime() > now;
  } else {
    const messagesSentAndNext = buildMessagesSentAndNext(event, eventDate);
    hasSentReminders = hasAnySentMessages(event, eventDate);
    hasScheduledReminders = hasAnyScheduledMessages(messagesSentAndNext);
  }

  const isPastEventDate = eventDate.getTime() < now;
  const isActivating =
    !eventDateIsReminderDate && isEventActivating(event, recentlyActivated);

  let status: EventReminderStatus = 'inactive';

  if (event.active && (hasSentReminders || hasScheduledReminders)) {
    status = hasSentReminders ? 'sent' : 'scheduled';
  }

  if (isActivating) {
    status = 'activating';
  }

  if (!isPastEventDate && eventNeedsReview(event)) {
    status = 'review';
  }

  if (isError || (event.errors?.length ?? 0) > 0) {
    status = 'error';
  }

  if (!remindersPaused) {
    return status;
  }
  return toPausedAccountStatus(status, event, isError);
}

function toPausedAccountStatus(
  status: EventReminderStatus,
  event: EventReminderStatusInput,
  isError: boolean | undefined,
): EventReminderStatus {
  if (status === 'inactive') {
    return 'no_reminder';
  }
  const onlyPauseError =
    event.errors?.length === 1 && PAUSE_EVENT_ERRORS.has(event.errors[0]);
  if (status === 'error' && !isError && onlyPauseError) {
    return 'no_reminder';
  }
  return status;
}

/**
 * True when an event has no receiver numbers but does have suggested numbers
 * awaiting the user's review. Ported from the web app's
 * `EventSuggestedNumbersHelper.needsReview`.
 */
export function eventNeedsReview(event: EventReminderStatusInput): boolean {
  return (
    !event.active &&
    !event.receiver_numbers?.length &&
    (event.suggested_numbers?.length ?? 0) > 0 &&
    (event.accepted_suggested_numbers?.length ?? 0) === 0 &&
    !event.suggestions_reviewed
  );
}

export function isEventActivating(
  event: EventReminderStatusInput,
  recentlyActivated: boolean,
): boolean {
  if (!recentlyActivated) {
    return false;
  }

  if ((event.errors?.length ?? 0) > 0) {
    return false;
  }

  const eventDate = getEventDate(event);
  const hasSentReminders = hasAnySentMessages(event, eventDate);
  const messagesSentAndNext = buildMessagesSentAndNext(event, eventDate);
  const hasScheduledReminders = hasAnyScheduledMessages(messagesSentAndNext);

  return !hasSentReminders && (!hasScheduledReminders || !event.active);
}

export function hasAnySentMessages(
  event: EventReminderStatusInput,
  eventDate: Date,
): boolean {
  const sentConfirmations = getSentConfirmationsForEvent(event, eventDate);
  const sentReminders = getSentRemindersForEvent(event, eventDate);
  const sentFollowUps = getSentFollowUpsForEvent(event, eventDate);
  return (
    sentConfirmations.length > 0 ||
    sentReminders.length > 0 ||
    sentFollowUps.length > 0
  );
}

/**
 * Narrower than `hasAnySentMessages`: only the `reminder` message, and only the
 * one belonging to this occurrence. A sent confirmation or follow-up says
 * nothing about whether the reminder will send, and follow-up matching is not
 * per-occurrence, so neither may stand in for a reminder send.
 */
export function hasSentReminderForOccurrence(
  event: SentReminderOccurrenceInput,
  occurrenceStart: Date,
): boolean {
  return getSentRemindersForEvent(event, occurrenceStart).length > 0;
}

export function hasAnyScheduledMessages(
  messagesSentAndNext: MessageSentAndNext,
): boolean {
  return !!(
    messagesSentAndNext.nextConfirmation ||
    messagesSentAndNext.nextReminder ||
    messagesSentAndNext.nextFollowUp
  );
}

export function buildMessagesSentAndNext(
  event: EventReminderStatusInput,
  eventDate: Date,
): MessageSentAndNext {
  const nextOccurrences =
    event.reminders
      ?.filter(
        (r) => r.next_occurrence && r.last_planner_status !== 'cancelled',
      )
      .sort(
        (a, b) =>
          new Date(a.next_occurrence!).getTime() -
          new Date(b.next_occurrence!).getTime(),
      ) || [];

  const sentReminders = getSentRemindersForEvent(event, eventDate);
  const sentFollowUps = getSentFollowUpsForEvent(event, eventDate);

  return {
    lastConfirmation: getLastSentConfirmation(event),
    nextConfirmation: getNextConfirmation(nextOccurrences),
    lastReminder: getLastSentMessage(sentReminders),
    nextReminder: getNextReminder(nextOccurrences),
    lastFollowUp: getLastSentMessage(sentFollowUps),
    nextFollowUp: getNextFollowUp(nextOccurrences),
  };
}

function toIsoString(value: string | Date | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  return value instanceof Date ? value.toISOString() : String(value);
}

function getSentConfirmationsForEvent(
  event: EventReminderStatusInput,
  eventDate: Date,
): EventReminderStatusSentReminder[] {
  return (
    event.sent_reminders
      ?.filter((r) => r.message_type === 'confirmation')
      .filter((r) =>
        isSameEventStart(event, eventDate, Number(r.event_date)),
      ) || []
  );
}

function getSentRemindersForEvent(
  event: SentReminderOccurrenceInput,
  eventDate: Date,
): EventReminderStatusSentReminder[] {
  return (
    event.sent_reminders
      ?.filter((r) => r.message_type === 'reminder')
      .filter((r) => {
        const occurrenceStartMs =
          new Date(r.occurrence_date).getTime() + r.time_before;
        return isSameEventStart(event, eventDate, occurrenceStartMs);
      }) || []
  );
}

// S-596: all-day starts compare by calendar day in calendars.timezone, the
// predicate the send gate uses, so the result does not depend on the viewer.
function isSameEventStart(
  event: SentReminderOccurrenceInput,
  eventDate: Date,
  occurrenceStartMs: number,
): boolean {
  const allDay = getAllDayCalendarDay(event);
  const occurrenceDay = allDay
    ? getCalendarDayKey(occurrenceStartMs, allDay.timeZone)
    : null;
  if (!allDay || !occurrenceDay) {
    return occurrenceStartMs === eventDate.getTime();
  }
  return occurrenceDay === allDay.dayKey;
}

function getAllDayCalendarDay(
  event: SentReminderOccurrenceInput,
): { dayKey: string; timeZone: string } | null {
  const dayKey = event.google_event_date?.trim();
  if (!event.all_day || !event.calendar_timezone || !dayKey) {
    return null;
  }
  if (!ALL_DAY_DATE_PATTERN.test(dayKey)) {
    return null;
  }
  return { dayKey, timeZone: event.calendar_timezone };
}

function getSentFollowUpsForEvent(
  event: EventReminderStatusInput,
  eventDate: Date,
): EventReminderStatusSentReminder[] {
  const duration =
    new Date(+(event.event_end_date ?? 0)).getTime() -
    new Date(+event.event_date).getTime();
  const currentEventEndDate = eventDate.getTime() + duration;
  return (
    event.sent_reminders
      ?.filter((r) => r.message_type === 'follow_up')
      .filter((r) => {
        const occurrenceDate = new Date(r.occurrence_date);
        const correspondingEventDate = new Date(
          occurrenceDate.getTime() - r.time_before,
        );
        return correspondingEventDate.getTime() === currentEventEndDate;
      }) || []
  );
}

function getLastSentConfirmation(
  event: EventReminderStatusInput,
): string | null {
  const confirmations =
    event.sent_reminders?.filter((r) => r.message_type === 'confirmation') ||
    [];
  return confirmations.length > 0
    ? toIsoString(
        confirmations.sort(
          (a, b) =>
            new Date(b.last_sending_date).getTime() -
            new Date(a.last_sending_date).getTime(),
        )[0].last_sending_date,
      )
    : null;
}

function getNextConfirmation(
  nextOccurrences: EventReminderStatusReminder[],
): string | null {
  const confirmation = nextOccurrences.find(
    (r) => r.message_type === 'confirmation',
  );
  return toIsoString(confirmation?.next_occurrence);
}

function getNextReminder(
  nextOccurrences: EventReminderStatusReminder[],
): string | null {
  const reminder = nextOccurrences.find((r) => r.message_type === 'reminder');
  return toIsoString(reminder?.next_occurrence);
}

function getNextFollowUp(
  nextOccurrences: EventReminderStatusReminder[],
): string | null {
  const followUp = nextOccurrences.find((r) => r.message_type === 'follow_up');
  return toIsoString(followUp?.next_occurrence);
}

function getLastSentMessage(
  sentMessages: EventReminderStatusSentReminder[],
): string | null {
  return sentMessages.length > 0
    ? toIsoString(
        sentMessages.sort(
          (a, b) =>
            new Date(b.last_sending_date).getTime() -
            new Date(a.last_sending_date).getTime(),
        )[0].last_sending_date,
      )
    : null;
}
